import { getReportFooterText } from '../reportFooter';
import type { PersonLabels } from '@/hooks/usePersonLabels';
export interface IeItemsNotOnPhrHeaderInfo {
  individualName?: string | null;
  billet?: string | null;
  linSearch?: string | null;
  ieFilter?: string | null;
}

export interface IeItemsNotOnPhrItem {
  individualName?: string | null;
  nomenclature: string;
  nsn?: string | null;
  lin?: string | null;
  qtyAuthorized?: number | null;
  qtyIssued?: number | null;
  qtyNeeded?: number | null;
}

export interface IeItemsNotOnPhrTemplateOptions {
  generatedAt: Date;
  header: IeItemsNotOnPhrHeaderInfo;
  items: IeItemsNotOnPhrItem[];
  personLabels?: PersonLabels;
  ieKitLabel?: string;
}

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const sanitizeText = (value: string | null | undefined): string => {
  const trimmed = value?.toString().trim();
  if (!trimmed) {
    return '—';
  }
  return escapeHtml(trimmed);
};

const formatDateTime = (value: Date): string =>
  new Intl.DateTimeFormat('en-US', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(value);

const buildItemsTable = (items: IeItemsNotOnPhrItem[], personLowerLabel = 'individual', ieKitLabel = 'IE Kit'): string => {
  if (items.length === 0) {
    return `
      <tr>
        <td colspan="7" class="empty-row">
          No ${ieKitLabel} items were missing from SM PHR for the selected ${personLowerLabel}.
        </td>
      </tr>
    `;
  }

  return items
    .map(
      (item) => `
        <tr>
          <td>${sanitizeText(item.individualName)}</td>
          <td>${sanitizeText(item.nomenclature)}</td>
          <td>${sanitizeText(item.nsn)}</td>
          <td>${sanitizeText(item.lin)}</td>
          <td class="numeric">${sanitizeText(
            item.qtyAuthorized !== null && item.qtyAuthorized !== undefined
              ? String(item.qtyAuthorized)
              : '',
          )}</td>
          <td class="numeric">${sanitizeText(
            item.qtyIssued !== null && item.qtyIssued !== undefined ? String(item.qtyIssued) : '',
          )}</td>
          <td class="numeric">${sanitizeText(
            item.qtyNeeded !== null && item.qtyNeeded !== undefined ? String(item.qtyNeeded) : '',
          )}</td>
        </tr>
      `,
    )
    .join('');
};

export const buildIeItemsNotOnPhrReportHtml = ({
  generatedAt,
  header,
  items,
  personLabels,
  ieKitLabel = 'IE Kit',
}: IeItemsNotOnPhrTemplateOptions): string => {
  const pl = personLabels ?? { label: 'Individual', labelPlural: 'Individuals', lower: 'individual', lowerPlural: 'individuals', article: 'an' as const };
  const generatedLabel = formatDateTime(generatedAt);
  const individualLabel = header.individualName
    ? sanitizeText(header.individualName)
    : '—';
  const billetLabel = header.billet ? sanitizeText(header.billet) : '—';
  const linSearchLabel = header.linSearch ? sanitizeText(header.linSearch) : '—';
  const ieFilterLabel = header.ieFilter ? sanitizeText(header.ieFilter) : '—';
  const itemsTable = buildItemsTable(items, pl.lower, ieKitLabel);

  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="utf-8" />
    <title>${ieKitLabel} Items Not on SM PHR</title>
    <style>
      :root {
        color-scheme: light;
      }

      * {
        box-sizing: border-box;
      }

      body {
        margin: 16px;
        font-family: 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        color: #15263c;
        background: #ffffff;
      }

      header {
        margin-bottom: 16px;
      }

      .title {
        font-size: 24px;
        font-weight: 700;
        margin: 0;
        text-align: center;
      }

      .meta-row {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        margin-top: 12px;
      }

      .meta-block {
        min-width: 220px;
      }

      .meta-label {
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 0.06em;
        text-transform: uppercase;
        color: #5c6f85;
        margin-bottom: 2px;
      }

      .meta-value {
        font-size: 14px;
        color: #15263c;
      }

      table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 16px;
        border: 1px solid #d7e3f4;
      }

      thead th {
        background: #e9f1ff;
        text-align: left;
        font-size: 12px;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        padding: 10px 12px;
        color: #4a5c72;
        border-bottom: 1px solid #d7e3f4;
      }

      tbody td {
        padding: 10px 12px;
        border-bottom: 1px solid #edf3fc;
        font-size: 14px;
        color: #1f2f45;
      }

      tbody tr:last-child td {
        border-bottom: none;
      }

      tbody tr:nth-child(even) td {
        background: #f9fbff;
      }

      .numeric {
        text-align: center;
        font-variant-numeric: tabular-nums;
      }

      .empty-row {
        text-align: center;
        font-style: italic;
        color: #6b7c92;
        padding: 24px 12px;
      }

      footer {
        margin-top: 24px;
        font-size: 12px;
        color: #5c6f85;
        text-align: center;
      }

      @media print {
        body {
          margin: 8mm;
        }
      }
    </style>
  </head>
  <body>
    <header>
      <h1 class="title">${ieKitLabel} Items Not on SM PHR</h1>
      <div class="meta-row">
        <div class="meta-block">
          <div class="meta-label">${pl.label}</div>
          <div class="meta-value">${individualLabel}</div>
        </div>
        <div class="meta-block">
          <div class="meta-label">Billet</div>
          <div class="meta-value">${billetLabel}</div>
        </div>
        <div class="meta-block">
          <div class="meta-label">LIN Search</div>
          <div class="meta-value">${linSearchLabel}</div>
        </div>
        <div class="meta-block">
          <div class="meta-label">${ieKitLabel}</div>
          <div class="meta-value">${ieFilterLabel}</div>
        </div>
      </div>
    </header>

    <section>
      <table>
        <thead>
          <tr>
            <th>${pl.label}</th>
            <th>Nomenclature</th>
            <th>NSN</th>
            <th>LIN</th>
            <th class="numeric">Qty Authorized</th>
            <th class="numeric">Qty Issued</th>
            <th class="numeric">Qty Needed</th>
          </tr>
        </thead>
        <tbody>
          ${itemsTable}
        </tbody>
      </table>
    </section>

    <footer>
      Generated ${escapeHtml(generatedLabel)} · ${getReportFooterText()}
    </footer>
  </body>
</html>`;
};
